import type { BlockKind, CellDefinition, Layout, Point, ProjectileProfile } from "../types";
import { tuning } from "./tuning";

export const WORLD_WIDTH = 930;
export const WORLD_HEIGHT = 834;
export const BALL_RADIUS = 22;
export const FLOOR_HEIGHT = 38;
export const MAX_PULL = 110;
export const PULL_DEAD_ZONE = 30;

export interface CellState extends CellDefinition {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  remaining: number;
  flash: number;
}

export interface BallState extends Point {
  vx: number;
  vy: number;
  active: boolean;
  elapsed: number;
  initialSpeed: number;
  /** Set once the player has blasted mid-flight; the shot then skips its automatic blast. */
  detonated?: boolean;
}

export interface Particle extends Point {
  vx: number;
  vy: number;
  life: number;
  color: string;
  size: number;
}

export interface EngineState {
  layout: Layout;
  cells: CellState[];
  ball: BallState;
  contactingCells: Set<string>;
  particles: Particle[];
  blast: { x: number; y: number; radius: number; life: number } | null;
  message: string;
}

export const materialColors: Record<BlockKind, string> = {
  glass: "#38b8f0",
  laminated: "#2985e0",
  brick: "#405478",
  core: "#0fc29a",
  resonance: "#8f5ee0",
  dampener: "#fa9e29",
};

export function createEngine(layout: Layout): EngineState {
  const { grid } = layout;
  return {
    layout,
    cells: grid.cells.map((cell) => ({
      ...cell,
      id: `${cell.column}.${cell.row}`,
      x: grid.origin.x + cell.column * (grid.cell_width + grid.gap),
      y: grid.origin.y + cell.row * (grid.cell_height + grid.gap),
      width: grid.cell_width,
      height: grid.cell_height,
      remaining: cell.durability,
      flash: 0,
    })),
    ball: { ...layout.slingshot, vx: 0, vy: 0, active: false, elapsed: 0, initialSpeed: 0 },
    contactingCells: new Set(),
    particles: [],
    blast: null,
    message: "",
  };
}

export function clampDrag(anchor: Point, point: Point): Point {
  const dx = point.x - anchor.x;
  const dy = point.y - anchor.y;
  const distance = Math.hypot(dx, dy);
  if (distance <= MAX_PULL || distance === 0) return point;
  return { x: anchor.x + (dx / distance) * MAX_PULL, y: anchor.y + (dy / distance) * MAX_PULL };
}

export function launchVelocity(anchor: Point, position: Point, profile: ProjectileProfile): Point | null {
  const dx = anchor.x - position.x;
  const dy = anchor.y - position.y;
  const distance = Math.hypot(dx, dy);
  if (distance < PULL_DEAD_ZONE) return null;
  const pullProgress = Math.min(1, Math.max(0, (distance - PULL_DEAD_ZONE) / (MAX_PULL - PULL_DEAD_ZONE)));
  const scoreSpeed = tuning.speedMin + (tuning.speedMax - tuning.speedMin) * Math.min(1, Math.max(0, profile.speedPosition));
  const speed = tuning.speedMin + (scoreSpeed - tuning.speedMin) * pullProgress;
  return { x: (dx / distance) * speed, y: (dy / distance) * speed };
}

export function retainedFactor(mass: number, kind: BlockKind): number {
  const massResistance = 1 + tuning.massResistance * (Math.sqrt(Math.max(1, mass)) - 1);
  return Math.min(1, Math.max(0, 1 - (1 - tuning.retainBase * tuning.materials[kind]) / massResistance));
}

export function penetrationFor(speed: number, durability: number, factor: number, limitSpeed: number) {
  let remainingSpeed = speed;
  let damage = 0;
  while (damage < durability && remainingSpeed * factor >= limitSpeed) {
    remainingSpeed *= factor;
    damage += 1;
  }
  return { damage, remainingSpeed, reflects: damage < durability };
}

function spawnShards(state: EngineState, cell: CellState) {
  const count = cell.kind === "brick" ? 8 : 12;
  for (let index = 0; index < count; index += 1) {
    const angle = (index / count) * Math.PI * 2 + 0.18;
    const speed = 55 + (index % 5) * 18;
    state.particles.push({
      x: cell.x,
      y: cell.y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      life: 1,
      color: materialColors[cell.kind],
      size: cell.kind === "brick" ? 9 : 7,
    });
  }
}

function destroyCell(state: EngineState, cell: CellState) {
  if (cell.remaining > 0) return;
  spawnShards(state, cell);
  if (cell.kind !== "resonance") return;
  for (const neighbor of state.cells) {
    if (neighbor.remaining <= 0) continue;
    const adjacent = Math.abs(neighbor.column - cell.column) + Math.abs(neighbor.row - cell.row) === 1;
    if (!adjacent) continue;
    neighbor.remaining = Math.max(0, neighbor.remaining - 1);
    neighbor.flash = 1;
    if (neighbor.remaining === 0) destroyCell(state, neighbor);
  }
}

function hitCell(state: EngineState, cell: CellState, profile: ProjectileProfile): boolean {
  const speed = Math.hypot(state.ball.vx, state.ball.vy);
  const { damage, remainingSpeed, reflects } = penetrationFor(
    speed, cell.remaining, retainedFactor(profile.mass, cell.kind), state.ball.initialSpeed * tuning.stopRatio,
  );
  cell.remaining -= damage;
  cell.flash = 1;
  state.message = cell.kind === "dampener" ? "ABSORB" : cell.kind === "resonance" && damage > 0 ? "ECHO" : cell.kind === "brick" && damage > 0 ? "CRUSH" : damage >= 3 ? "SMASH" : damage === 2 ? "BREAK" : damage === 1 ? "CRACK" : "PING";
  if (cell.remaining === 0) destroyCell(state, cell);
  const ratio = speed > 0 ? remainingSpeed / speed : 0;
  state.ball.vx *= ratio;
  state.ball.vy *= ratio;
  return reflects;
}

function collideWithCell(state: EngineState, cell: CellState, profile: ProjectileProfile): "none" | "contact" | "reflected" {
  if (cell.remaining <= 0) return "none";
  const closestX = Math.max(cell.x - cell.width / 2, Math.min(state.ball.x, cell.x + cell.width / 2));
  const closestY = Math.max(cell.y - cell.height / 2, Math.min(state.ball.y, cell.y + cell.height / 2));
  const dx = state.ball.x - closestX;
  const dy = state.ball.y - closestY;
  if (dx * dx + dy * dy > BALL_RADIUS * BALL_RADIUS) return "none";

  if (state.contactingCells.has(cell.id)) return "contact";
  if (!hitCell(state, cell, profile)) return "contact";

  // Return the ball to the entry face so that a surviving block cannot be crossed.
  if (Math.abs(state.ball.vx) >= Math.abs(state.ball.vy)) {
    const direction = Math.sign(state.ball.vx) || 1;
    state.ball.x = cell.x - direction * (cell.width / 2 + BALL_RADIUS + 1);
    state.ball.vx *= -1;
  } else {
    const direction = Math.sign(state.ball.vy) || 1;
    state.ball.y = cell.y - direction * (cell.height / 2 + BALL_RADIUS + 1);
    state.ball.vy *= -1;
  }
  return "reflected";
}

export function applyBlast(state: EngineState, profile: ProjectileProfile) {
  if (profile.blastRadius <= 0) return;
  state.blast = { x: state.ball.x, y: state.ball.y, radius: profile.blastRadius, life: 1 };
  for (const cell of state.cells) {
    if (cell.remaining <= 0) continue;
    const allowance = Math.hypot(cell.width, cell.height) * 0.25;
    if (Math.hypot(cell.x - state.ball.x, cell.y - state.ball.y) > profile.blastRadius + allowance) continue;
    cell.remaining = Math.max(0, cell.remaining - 1);
    cell.flash = 1;
    if (cell.remaining === 0) destroyCell(state, cell);
  }
  state.message = "BLAST";
}

/** Manual mid-flight blast, once per shot. Returns false when the ball cannot detonate now. */
export function detonate(state: EngineState, profile: ProjectileProfile): boolean {
  const ball = state.ball;
  if (!ball.active || ball.detonated || !profile.canDetonate || profile.blastRadius <= 0) return false;
  applyBlast(state, profile);
  ball.vx *= tuning.detonateSpeedKeep;
  ball.vy *= tuning.detonateSpeedKeep;
  ball.detonated = true;
  return true;
}

export function stepEffects(state: EngineState, dt: number): boolean {
  const step = Math.min(dt, 1 / 30);
  for (const cell of state.cells) cell.flash = Math.max(0, cell.flash - step * 4);
  state.particles = state.particles.filter((particle) => {
    particle.x += particle.vx * step;
    particle.y += particle.vy * step;
    particle.vy -= 240 * step;
    particle.life -= step * 1.7;
    return particle.life > 0;
  });
  if (state.blast) {
    state.blast.life -= step * 2.8;
    if (state.blast.life <= 0) state.blast = null;
  }
  return state.particles.length > 0 || state.blast !== null || state.cells.some((cell) => cell.flash > 0);
}

export function stepEngine(state: EngineState, profile: ProjectileProfile, dt: number): "running" | "finished" {
  const ball = state.ball;
  if (!ball.active) return "finished";
  const step = Math.min(dt, 1 / 30);
  const decay = Math.exp(-tuning.airDrag * step);
  ball.vy += -tuning.gravity * step;
  ball.vx *= decay;
  ball.vy *= decay;
  ball.x += ball.vx * step;
  ball.y += ball.vy * step;
  ball.elapsed += step;

  if (ball.y - BALL_RADIUS <= FLOOR_HEIGHT && ball.vy < 0) {
    ball.y = FLOOR_HEIGHT + BALL_RADIUS;
    ball.vy *= -tuning.floorBounce;
    ball.vx *= tuning.floorFriction;
  }

  const contacts = new Set<string>();
  for (const cell of state.cells) {
    const result = collideWithCell(state, cell, profile);
    if (result !== "none") contacts.add(cell.id);
    if (result === "reflected") break;
  }
  state.contactingCells = contacts;

  stepEffects(state, step);

  const outside = ball.x < -100 || ball.x > WORLD_WIDTH + 100 || ball.y < -100 || ball.y > WORLD_HEIGHT + 180;
  const stalled = Math.hypot(ball.vx, ball.vy) <= ball.initialSpeed * tuning.stopRatio;
  if (outside || stalled || ball.elapsed >= tuning.maxFlightSeconds) {
    if ((!outside || tuning.blastOnExit) && !ball.detonated) applyBlast(state, profile);
    ball.active = false;
    return "finished";
  }
  return "running";
}

export function remaining(state: EngineState) {
  return {
    blocks: state.cells.filter((cell) => cell.remaining > 0).length,
    goals: state.cells.filter((cell) => cell.remaining > 0 && cell.goal).length,
  };
}
